// src/components/MediaTracker.js - Record 613 (Bob 10/8). RULING 157 ("agreed"): the tracker for ALL classes of
// recording on a claim or a discipline case. A carrier-produced recording is stored and its row links the uploaded file;
// a recording made by the organization or by a member is NOT stored (storage.rules recordingAllowed(); the claim-upload
// callable) - its row says who made it, who holds it and where it is. Rows are records: nobody but the administrator
// changes or removes one (firestore.rules mediaItems).
//
//   parentKind  'claim' | 'discipline'
//   parentId    the claim's or case's document id
//   parent      the claim or case itself (who may add a row is read from it)
//   documents   its uploaded files (a carrier row may link one of them)
//   user        the signed-in account
//
// The rules decide; the shapes and the "may he add" mirror are src\data\mediaItems.js (scripts\test-media-tracker.mjs).
import React, { useEffect, useState } from 'react';
import { db, storage } from '../firebase';
import { collection, addDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { viewStoredFile } from '../data/scannedFile';   // ruling 247: View fetches the link when clicked, after the scan
import {
  TRACKER_TITLE, MEDIA_CLASSES, MEDIA_KINDS, CLASS_LABELS, KIND_LABELS, LIMITS,
  validateMediaItem, carrierFileChoices, mayAddMediaItem, sortItems,
} from '../data/mediaItems';

const BLANK = { class: 'carrier', kind: 'audio', description: '', recordedOn: '', recordedBy: '', heldBy: '', where: '', storagePath: '' };
const label = { fontSize: 10, color: '#8AA4BF', textTransform: 'uppercase', letterSpacing: 1, display: 'block', marginBottom: 4 };

function when(ts) {
  try { return ts && typeof ts.toDate === 'function' ? ts.toDate().toLocaleDateString() : ''; } catch (e) { return ''; }
}

export default function MediaTracker({ parentKind, parentId, parent, documents, user }) {
  const col = parentKind === 'discipline' ? 'disciplineCases' : 'claims';
  const [items, setItems] = useState([]);
  const [loadErr, setLoadErr] = useState('');
  const [form, setForm] = useState(BLANK);
  const [errs, setErrs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!parentId) return undefined;
    return onSnapshot(collection(db, col, parentId, 'mediaItems'),
      (snap) => { setItems(sortItems(snap.docs.map(d => ({ id: d.id, ...d.data() })))); setLoadErr(''); },
      (e) => { setItems([]); setLoadErr((e && e.code) || 'error'); });
  }, [col, parentId]);

  const canAdd = mayAddMediaItem(user, parentKind, parent);
  const files = carrierFileChoices(parentKind, parentId, documents);
  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v, ...(k === 'class' && v !== 'carrier' ? { storagePath: '' } : {}) }));

  const add = async () => {
    const v = validateMediaItem(form, { parentKind, parentId });
    if (!v.ok) { setErrs(v.errors); return; }
    setBusy(true); setErrs([]);
    try {
      await addDoc(collection(db, col, parentId, 'mediaItems'),
        { ...v.item, createdBy: (user && user.uid) || '', createdAt: serverTimestamp() });
      setForm(BLANK); setOpen(false);
    } catch (e) {
      setErrs(['Not recorded: ' + ((e && e.code) || (e && e.message) || 'the write was refused') + '. Your entries are still in the form.']);
    }
    setBusy(false);
  };

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        <div className="card-title">{TRACKER_TITLE}</div>
        <div style={{ fontSize: 11, color: '#8AA4BF' }}>{items.length} recording{items.length !== 1 ? 's' : ''} listed</div>
      </div>
      <div style={{ fontSize: 11, color: '#8AA4BF', marginBottom: 10, lineHeight: 1.6 }}>
        Every recording that bears on this {parentKind === 'discipline' ? 'case' : 'claim'}, whoever made it. Only a
        carrier-produced recording is stored on this platform, and its row links the uploaded file. A recording made by the
        organization or by a member is not stored here: its row records who made it, who holds it and where it is.
        A row is a record - it is not edited or removed once written.
      </div>
      {loadErr ? <div style={{ fontSize: 12, color: '#E06C6C', marginBottom: 8 }}>The list could not be read ({loadErr}).</div> : null}
      {items.length ? (
        <div className="table-wrapper"><table>
          <thead><tr><th>Whose</th><th>Kind</th><th>What</th><th>Recorded</th><th>Held by</th><th>Where</th><th>Listed</th></tr></thead>
          <tbody>{items.map(r => (
            <tr key={r.id}>
              <td>{CLASS_LABELS[r.class] || r.class}</td>
              <td>{KIND_LABELS[r.kind] || r.kind}</td>
              <td style={{ whiteSpace: 'pre-wrap' }}>{r.description}</td>
              <td>{r.recordedOn || 'date not known'}{r.recordedBy ? ' - ' + r.recordedBy : ''}</td>
              <td>{r.heldBy || ''}</td>
              <td style={{ wordBreak: 'break-word' }}>
                {r.where || ''}
                {r.class === 'carrier' && r.storagePath ? (
                  <div><button type="button" className="btn btn-secondary" onClick={viewStoredFile(storage, '', r.storagePath)} style={{ fontSize: 11, padding: '2px 8px', marginTop: 4 }}>View the stored file</button></div>
                ) : null}
              </td>
              <td style={{ fontSize: 11 }}>{when(r.createdAt)}</td>
            </tr>
          ))}</tbody>
        </table></div>
      ) : !loadErr ? <div style={{ fontSize: 12, color: 'var(--steel)', marginBottom: 8 }}>No recording is listed yet.</div> : null}

      {canAdd && !open ? (
        <button className="btn btn-secondary" style={{ marginTop: 10, fontSize: 12 }} onClick={() => setOpen(true)}>+ List a recording</button>
      ) : null}
      {canAdd && open ? (
        <div style={{ marginTop: 10, padding: 10, border: '1px solid #2E4A6B', borderRadius: 6, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div><span style={label}>Whose recording</span>
            <select aria-label="What class of recording this is" className="form-select" value={form.class} onChange={e => set('class', e.target.value)}>
              {MEDIA_CLASSES.map(c => <option key={c} value={c}>{CLASS_LABELS[c]}</option>)}
            </select></div>
          <div><span style={label}>Kind</span>
            <select aria-label="What kind of recording this is" className="form-select" value={form.kind} onChange={e => set('kind', e.target.value)}>
              {MEDIA_KINDS.map(k => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
            </select></div>
          <div style={{ gridColumn: '1 / span 2' }}><span style={label}>What it is a recording of</span>
            <textarea aria-label="Description of the recording" className="form-input" rows={2} maxLength={LIMITS.description} value={form.description} onChange={e => set('description', e.target.value)} /></div>
          <div><span style={label}>Date recorded (blank if not known)</span>
            <input aria-label="Recorded on" className="form-input" type="date" value={form.recordedOn} onChange={e => set('recordedOn', e.target.value)} /></div>
          <div><span style={label}>Recorded by</span>
            <input aria-label="Recorded by" className="form-input" maxLength={LIMITS.recordedBy} value={form.recordedBy} onChange={e => set('recordedBy', e.target.value)} /></div>
          <div><span style={label}>Held by (who has it now)</span>
            <input aria-label="Held by" className="form-input" maxLength={LIMITS.heldBy} value={form.heldBy} onChange={e => set('heldBy', e.target.value)} /></div>
          <div><span style={label}>Where it is (place, or a link)</span>
            <input aria-label="Where the recording is held" className="form-input" maxLength={LIMITS.where} value={form.where} onChange={e => set('where', e.target.value)} /></div>
          {form.class === 'carrier' ? (
            <div style={{ gridColumn: '1 / span 2' }}><span style={label}>The stored file (carrier-produced only)</span>
              <select aria-label="Storage path" className="form-select" value={form.storagePath} onChange={e => set('storagePath', e.target.value)}>
                <option value="">{files.length ? 'No file linked' : 'No file has been uploaded to this ' + (parentKind === 'discipline' ? 'case' : 'claim')}</option>
                {files.map(f => <option key={f.storagePath} value={f.storagePath}>{f.label}</option>)}
              </select></div>
          ) : (
            <div style={{ gridColumn: '1 / span 2', fontSize: 11, color: '#E8A020' }}>
              Not stored on this platform - say who holds it and where it is.
            </div>
          )}
          {errs.length ? <div style={{ gridColumn: '1 / span 2', color: '#E06C6C', fontSize: 12 }}>{errs.map((t, i) => <div key={i}>&#9888; {t}</div>)}</div> : null}
          <div style={{ gridColumn: '1 / span 2', display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" disabled={busy} onClick={add}>{busy ? 'Recording...' : 'List it'}</button>
            <button className="btn btn-secondary" disabled={busy} onClick={() => { setOpen(false); setErrs([]); }}>Cancel</button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
